/**
 * main.js — entry point. Boots the chrome, then routes.
 *
 * Routing note: the locale prefix is handled entirely by Django's
 * LocalePrefixMiddleware, so the client matches against the *stripped* path.
 * Switching language is a real navigation to /en/... rather than a re-render,
 * which is what makes the two locales separately linkable and indexable.
 */

import {qs, apiGet, emptyState, icon} from './lib/dom.js';
import {gettext, getLang, localePath, stripLocale, loadCatalog} from './lib/i18n.js';
import {
    loadNav, initDrawer, initSearch, initStickyHeader, initLangSwitch, renderFooter,
} from './ui/header.js';
import {initSliders, destroySliders} from './ui/slider.js';
import {initLightbox} from './ui/lightbox.js';
import {renderHome} from './pages/home.js';
import {initHeroParticles, destroyHeroParticles} from './ui/particles.js';
import {renderProductList, initProductList} from './pages/products.js';
import {renderProductDetail, initProductDetail} from './pages/product.js';
import {
    renderStaticPage, renderLab, renderNewsList, renderNewsDetail, renderSearch,
    renderResume,
} from './pages/content.js';

const main = qs('#main');

/* ------------------------------------------------------------------ *
 * Router
 * ------------------------------------------------------------------ */

function showLoading() {
    main.innerHTML = `
    <div class="nv-route-loading" role="status" aria-live="polite">
      <span class="nv-spinner" aria-hidden="true"></span>
      <span class="nv-sr-only">${gettext('Loading')}</span>
    </div>`;
}

function showError(message) {
    main.innerHTML = `
    <div class="nv-container nv-section">
      ${emptyState({
        iconName: 'triangle-exclamation',
        title: gettext('Something went wrong'),
        text: message,
        action: `<a href="${localePath('/')}" class="nv-btn nv-btn--primary">
                   ${icon('house')}<span>${gettext('Back home')}</span>
                 </a>`,
    })}
    </div>`;
}

function showNotFound() {
    main.innerHTML = `
    <div class="nv-container nv-section">
      ${emptyState({
        iconName: 'compass',
        title: gettext('Page not found'),
        text: gettext('That address does not exist, or it has moved.'),
        action: `<a href="${localePath('/')}" class="nv-btn nv-btn--primary">
                   ${icon('house')}<span>${gettext('Back home')}</span>
                 </a>`,
    })}
    </div>`;
}

/** Route table. Each entry gets the regex captures plus the query params. */
const routes = [
    {
        pattern: /^\/$/,
        async handle() {
            const data = await apiGet('/api/home/');
            main.innerHTML = renderHome(data);
            initSliders(main);
            initHeroParticles(main);
        },
    },
    {
        pattern: /^\/products\/?$/,
        async handle(_m, params) {
            const data = await apiGet('/api/products/', {
                q: params.get('q') || '',
                category: params.get('category') || '',
                type: params.get('type') || '',
                page: params.get('page') || '',
            });
            main.innerHTML = renderProductList(data, {
                q: params.get('q') || '',
                category: params.get('category') || '',
            });
            initProductList(main, navigate);
        },
    },
    {
        pattern: /^\/products\/([^/]+)\/?$/,
        async handle(match) {
            const data = await apiGet(`/api/products/${encodeURIComponent(match[1])}/`);
            main.innerHTML = renderProductDetail(data);
            initProductDetail(main);
        },
    },
    {
        pattern: /^\/page\/([^/]+)\/?$/,
        async handle(match) {
            const data = await apiGet(`/api/page/${encodeURIComponent(match[1])}/`);
            main.innerHTML = renderStaticPage(data);
            initSliders(main);
        },
    },
    {
        pattern: /^\/lab\/([^/]+)\/?$/,
        async handle(match) {
            const data = await apiGet(`/api/lab/${encodeURIComponent(match[1])}/`);
            main.innerHTML = renderLab(data);
            initSliders(main);
            initHeroParticles(main);
        },
    },
    {
        pattern: /^\/news\/?$/,
        async handle() {
            const data = await apiGet('/api/news/');
            main.innerHTML = renderNewsList(data);
        },
    },
    {
        pattern: /^\/news\/([^/]+)\/?$/,
        async handle(match) {
            const data = await apiGet(`/api/news/${encodeURIComponent(match[1])}/`);
            main.innerHTML = renderNewsDetail(data);
        },
    },
    {
        pattern: /^\/resume\/([^/]+)\/?$/,
        async handle(match) {
            const data = await apiGet(`/api/resume/${encodeURIComponent(match[1])}/`);
            main.innerHTML = renderResume(data);
        },
    },
    {
        pattern: /^\/search\/?$/,
        async handle(_m, params) {
            const query = params.get('q') || '';
            const data = query ? await apiGet('/api/search/', {q: query}) : {results: []};
            main.innerHTML = renderSearch(data, query);
        },
    },
];

/**
 * `location.pathname` hands back percent-encoded bytes for any non-ASCII slug,
 * so a Persian product slug arrived here already encoded. Route handlers then
 * called encodeURIComponent on it again and asked the API for a double-encoded
 * slug, which of course matched nothing — hence the "page not found" on
 * /products/تکنوتینک-technothink. Decode once, up front, and the handlers'
 * single encode is correct.
 */
function decodePath(path) {
    try {
        return decodeURIComponent(path);
    } catch (err) {
        return path;
    }
}

async function router() {
    const path = decodePath(stripLocale(window.location.pathname));
    const params = new URLSearchParams(window.location.search);

    destroySliders();
    destroyHeroParticles();
    showLoading();

    const route = routes.find((r) => r.pattern.test(path));
    if (!route) {
        showNotFound();
        return;
    }

    try {
        await route.handle(path.match(route.pattern), params);
    } catch (err) {
        if (err.status === 404) showNotFound();
        else {
            showError(gettext('There was a problem loading data from the server.'));
        }
    }

    // Reflect the new route in the nav's active state.
    markActiveNav();
}

export function navigate(path) {
    const target = path.startsWith('/') ? path : localePath(path);
    if (window.location.pathname + window.location.search === target) {
        router();
        return;
    }
    history.pushState(null, '', target);
    router();
    window.scrollTo({top: 0, behavior: 'instant' in window ? 'instant' : 'auto'});
}

function markActiveNav() {
    const current = stripLocale(window.location.pathname).replace(/\/$/, '') || '/';
    document.querySelectorAll('.nv-nav__item').forEach((item) => {
        const link = item.querySelector('.nv-nav__link');
        if (!link) return;
        const href = stripLocale(new URL(link.href, window.location.origin).pathname)
            .replace(/\/$/, '') || '/';
        const active = href === '/' ? current === '/' : current.startsWith(href);
        item.classList.toggle('is-active', active);
    });
}

/* ------------------------------------------------------------------ *
 * Link interception
 * ------------------------------------------------------------------ */

function initLinkRouting() {
    document.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link) return;

        // Leave language switches to the browser: they are real navigations that
        // must hit Django so the server re-renders with the right dir/lang.
        if (link.dataset.langLink) return;

        const href = link.getAttribute('href');
        if (!href || href.startsWith('#')) return;
        if (link.target === '_blank' || link.hasAttribute('download')) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;

        // Dashboard and admin are server-rendered.
        if (/^\/(dashboard|admin|static|media)\b/.test(url.pathname)) return;

        event.preventDefault();
        navigate(url.pathname + url.search);
    });

    window.addEventListener('popstate', router);
}

function initErrorActions() {
    document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-error-action]');
        if (!btn) return;
        event.preventDefault();
        if (btn.dataset.errorAction === 'back') window.history.back();
        if (btn.dataset.errorAction === 'reload') window.location.reload();
    });
}

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

async function boot() {
    document.documentElement.setAttribute('lang', getLang());

    initStickyHeader();
    initDrawer();
    initSearch(navigate);
    initLightbox();
    initLinkRouting();
    initLangSwitch();
    initErrorActions();

    await loadCatalog();

    const navData = await loadNav();
    router();
    renderFooter(navData);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
